// Design tokens and gender theme presets.
//
// The visual tokens (bg, ink, line, etc.) live in src/index.css under the
// Tailwind @theme block. They're available as Tailwind utilities (bg-bg,
// text-ink-2, border-line, font-serif, ...).
//
// This module owns the *gender presets*: only the accent colours change. The
// active preset is written to the document root as CSS custom properties via
// `applyTheme()`, so any Tailwind class like `bg-accent` or `text-accent-ink`
// re-renders for free.

export type GenderPreset = 'boy' | 'girl' | 'neutral';

export interface ThemeTokens {
  accent: string;
  accentSoft: string;
  accentInk: string;
  name: string;
}

export const themes: Record<GenderPreset, ThemeTokens> = {
  boy: {
    accent: 'oklch(0.66 0.045 245)',
    accentSoft: 'oklch(0.66 0.045 245 / 0.10)',
    accentInk: 'oklch(0.42 0.05 245)',
    name: 'Boy',
  },
  girl: {
    accent: 'oklch(0.72 0.055 20)',
    accentSoft: 'oklch(0.72 0.055 20 / 0.10)',
    accentInk: 'oklch(0.46 0.07 22)',
    name: 'Girl',
  },
  neutral: {
    accent: 'oklch(0.70 0.05 150)',
    accentSoft: 'oklch(0.70 0.05 150 / 0.10)',
    accentInk: 'oklch(0.44 0.06 150)',
    name: 'Neutral',
  },
};

/**
 * Apply a gender preset by setting CSS custom properties on the document root.
 * Tailwind utilities that reference --color-accent, --color-accent-soft and
 * --color-accent-ink pick this up automatically.
 */
export function applyTheme(preset: GenderPreset): void {
  const t = themes[preset];
  const root = document.documentElement;
  root.style.setProperty('--color-accent', t.accent);
  root.style.setProperty('--color-accent-soft', t.accentSoft);
  root.style.setProperty('--color-accent-ink', t.accentInk);
}

// Legacy export so any component still reading raw hex values keeps working
// during the migration. Once every screen is on Tailwind utilities, this can
// be deleted.
export const tokens = {
  bg: 'var(--color-bg)',
  surface: 'var(--color-surface)',
  ink: 'var(--color-ink)',
  ink2: 'var(--color-ink-2)',
  ink3: 'var(--color-ink-3)',
  line: 'var(--color-line)',
  hairline: 'var(--color-hairline)',
  tintBg: 'var(--color-tint)',
  amber: 'var(--color-amber)',
  amberSoft: 'var(--color-amber-soft)',
  grey: 'var(--color-grey)',
  greySoft: 'var(--color-grey-soft)',
  serif: 'var(--font-serif)',
  sans: 'var(--font-sans)',
} as const;
